import { Loader2 } from "lucide-react";
import { Navigate, Outlet, Route, Routes, useLocation, useParams } from "react-router-dom";
import { Toaster } from "react-hot-toast";
import { useSession } from "@/auth/SessionProvider";
import { authSignInUrl } from "@/lib/apiOrigin";
import { AtwWorkforceLockup } from "@/components/Brand/AtwProductMark";
import { AgentsShell } from "@/components/Agents/AgentsShell";
import { AgentsHomePage } from "@/pages/AgentsHomePage";
import { AgentsRosterPage } from "@/pages/AgentsRosterPage";
import { OverseerPage } from "@/pages/OverseerPage";
import { ConnectionsPage } from "@/pages/ConnectionsPage";
import { CreateConnectionPage } from "@/pages/CreateConnectionPage";
import { ConnectionsEmailPage } from "@/pages/ConnectionsEmailPage";
import { CreateSharedInboxPage } from "@/pages/CreateSharedInboxPage";
import { SchedulesPage } from "@/pages/SchedulesPage";
import { CreateSchedulePage } from "@/pages/CreateSchedulePage";
import { CreditsPage } from "@/pages/CreditsPage";
import { UsersPage } from "@/pages/UsersPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { BillingPage } from "@/pages/BillingPage";
import { CheckoutPage } from "@/pages/CheckoutPage";
import { AgentDetailPage } from "@/pages/AgentDetailPage";
import { CreateAgentPage } from "@/pages/CreateAgentPage";
import {
  agentSectionPath,
  parseLegacyAgentSectionPath,
} from "@/lib/agentNav";
import { agentsHref } from "@/lib/atwAgentsHost";
import { KnowledgeLayout } from "@/components/Agents/knowledge/KnowledgeLayout";
import { KnowledgeOverviewPage } from "@/pages/knowledge/KnowledgeOverviewPage";
import { LibrariesPage } from "@/pages/knowledge/LibrariesPage";
import { KnowledgeLegacyRedirect } from "@/pages/knowledge/KnowledgeLegacyRedirect";
import { LibraryPage } from "@/pages/knowledge/LibraryPage";
import { BooksPage } from "@/pages/knowledge/BooksPage";
import { BookPage } from "@/pages/knowledge/BookPage";
import { ArticlesPage } from "@/pages/knowledge/ArticlesPage";
import { ArticleViewPage } from "@/pages/knowledge/ArticleViewPage";
import { ArticlePage } from "@/pages/knowledge/ArticlePage";
import { WikiPage } from "@/pages/WikiPage";

/** Soft gate like One: never auto-bounce to auth (redirect loops feel like auto logout). */

export function App() {
  return (
    <>
      <Toaster position="top-center" />
      <Routes>
        <Route
          element={
            <AuthGate>
              <AgentsShell>
                <Outlet />
              </AgentsShell>
            </AuthGate>
          }
        >
          <Route index element={<AgentsHomePage />} />
          <Route path="agents" element={<AgentsRosterPage />} />
          <Route path="knowledge" element={<KnowledgeLayout />}>
            <Route index element={<Navigate to="bases" replace />} />
            <Route path="bases" element={<KnowledgeOverviewPage />} />
            <Route path="bases/:baseId" element={<KnowledgeOverviewPage />} />
            <Route path="bases/:baseId/libraries" element={<LibrariesPage />} />
            <Route path="bases/:baseId/libraries/:libraryId" element={<LibraryPage />} />
            <Route path="bases/:baseId/books" element={<BooksPage />} />
            <Route path="bases/:baseId/books/:bookKey" element={<BookPage />} />
            <Route path="bases/:baseId/articles" element={<ArticlesPage />} />
            <Route path="bases/:baseId/articles/new" element={<ArticlePage mode="create" />} />
            <Route path="bases/:baseId/articles/:articleId/edit" element={<ArticlePage mode="edit" />} />
            <Route path="bases/:baseId/articles/:articleId" element={<ArticleViewPage />} />
            <Route path="libraries" element={<KnowledgeLegacyRedirect />} />
            <Route path="libraries/:libraryId" element={<KnowledgeLegacyRedirect />} />
            <Route path="books" element={<KnowledgeLegacyRedirect />} />
            <Route path="books/:bookKey" element={<KnowledgeLegacyRedirect />} />
            <Route path="articles" element={<KnowledgeLegacyRedirect />} />
            <Route path="articles/new" element={<KnowledgeLegacyRedirect />} />
            <Route path="articles/:articleId/edit" element={<KnowledgeLegacyRedirect />} />
            <Route path="articles/:articleId" element={<KnowledgeLegacyRedirect />} />
          </Route>
          <Route path="wiki" element={<WikiPage />} />
          <Route path="overseer" element={<OverseerPage />} />
          <Route path="connections/new" element={<CreateConnectionPage />} />
          <Route path="connections/email/new" element={<CreateSharedInboxPage />} />
          <Route path="connections/email" element={<ConnectionsEmailPage />} />
          <Route path="connections" element={<ConnectionsPage />} />
          <Route path="schedules/new" element={<CreateSchedulePage />} />
          <Route path="schedules" element={<SchedulesPage />} />
          <Route path="credits" element={<CreditsPage />} />
          <Route path="users" element={<UsersPage />} />
          <Route path="settings" element={<SettingsPage />} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="checkout" element={<CheckoutPage />} />
          <Route path="agents/new" element={<CreateAgentPage />} />
          <Route path="agents/:agentId" element={<AgentDetailPage />} />
          <Route path="agents/:agentId/timeline" element={<AgentDetailPage />} />
          <Route
            path="agents/:agentId/settings/email"
            element={<AgentEmailSettingsRedirect />}
          />
          <Route
            path="agents/:agentId/settings/email/personal"
            element={<AgentDetailPage />}
          />
          <Route
            path="agents/:agentId/settings/email/group"
            element={<AgentDetailPage />}
          />
          <Route path="agents/:agentId/settings/:setting" element={<AgentDetailPage />} />
          <Route path="agents/:agentId/:section" element={<LegacyAgentSectionRedirect />} />
          <Route path="seats/:agentId" element={<LegacySeatRedirect />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

function LegacySeatRedirect() {
  const { agentId } = useParams();
  return <Navigate to={agentId ? `/agents/${agentId}` : "/"} replace />;
}

function AgentEmailSettingsRedirect() {
  const { agentId } = useParams();
  if (!agentId) {
    return <Navigate to={agentsHref("/agents")} replace />;
  }
  return (
    <Navigate to={agentsHref(agentSectionPath(agentId, "email", "personal"))} replace />
  );
}

function LegacyAgentSectionRedirect() {
  const location = useLocation();
  const { agentId } = useParams();
  const legacy = parseLegacyAgentSectionPath(location.pathname);
  if (legacy) {
    return <Navigate to={agentsHref(agentSectionPath(legacy.agentId, legacy.section))} replace />;
  }
  return <Navigate to={agentsHref(agentId ? `/agents/${agentId}` : "/agents")} replace />;
}

function AuthGate({ children }: { children: React.ReactNode }) {
  const session = useSession();
  const location = useLocation();
  const callbackPath = `${location.pathname}${location.search}`;

  if (session.status === "loading") {
    return (
      <div className="flex h-[100dvh] items-center justify-center bg-slate-100 dark:bg-zinc-950">
        <Loader2 className="size-8 animate-spin text-sky-600 dark:text-sky-300" aria-hidden />
        <span className="sr-only">Loading session</span>
      </div>
    );
  }

  if (session.status === "unauthenticated") {
    return (
      <div className="flex h-[100dvh] flex-col items-center justify-center gap-4 bg-slate-100 px-6 dark:bg-zinc-950">
        <div className="rounded-xl bg-[#111829] px-5 py-4">
          <AtwWorkforceLockup />
        </div>
        <p className="max-w-md text-center text-sm text-slate-600 dark:text-slate-400">
          Sign in with your ATW account to open Workforce.
        </p>
        <a
          href={authSignInUrl(callbackPath || "/")}
          className="rounded-lg bg-sky-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-sky-500"
        >
          Sign in
        </a>
      </div>
    );
  }

  return children;
}
